import { useEffect, useState } from "react";
import { IScope } from "angular";
import { react2angular } from "react2angular";
import PropTypes from "prop-types";
import { getService } from "react-in-angularjs";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import { ICollection, ITheme } from "../../../types/ICollection";
import { IHasMapAccess } from "../../../../app/routes/mapping/services/has-map-access";
import { withReduxProvider } from "../../../services/withReduxProvider";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { TruTerritorySideFilters } from "./TruTerritorySideFilters";
import { IFilter } from "../../../types/IMap";
import { SortableUl } from "../../../components/Sortable/SortableUl";
import { CollectionLi } from "./CollectionLi";

interface TruTerritorySideProps {
  close: () => void;
  isPublicPage?: boolean;
  applyChanges: () => void;
  removeCollection?: (ID: ICollection["ID"]) => void;
  toggleVisible: (ID: ICollection["ID"]) => void;
  setMapLabel: (ID: ICollection["ID"], label: string | null) => void;
  setMapTheme: (ID: ICollection["ID"], theme: ITheme) => void;
  addTheme?: (ID: ICollection["ID"]) => void;
  editTheme?: (ID: ICollection["ID"]) => void;
  cloneTheme?: (ID: ICollection["ID"], theme: ITheme) => void;
  setFilters?: (ID: ICollection["ID"], filters: IFilter[]) => void;
  setCollections: (collection: ICollection[]) => void;
  $scope: IScope;
}

export const TruTerritorySide: React.FC<TruTerritorySideProps & JSX.IntrinsicAttributes> = ({
  isPublicPage,
  close,
  removeCollection,
  toggleVisible,
  setMapLabel,
  setMapTheme,
  addTheme: ogAddTheme,
  editTheme: ogEditTheme,
  cloneTheme: ogCloneTheme,
  setFilters,
  setCollections,
  $scope,
}: TruTerritorySideProps) => {
  const hasAccess: IHasMapAccess = getService("hasMapAccess");
  const { map, currentThemes } = useAppSelector((state) => state.pages.truterritory);

  const [openCollections, setOpenCollections] = useState<Record<number, boolean>>({});
  const [selectedCollection, setSelectedCollection] = useState<ICollection>();

  // when the map changes, close all collections and go back to the collections view
  useEffect(() => {
    setOpenCollections({});
    setSelectedCollection(undefined);
  }, [map?.ID]);

  const onClose = () => $scope.$apply(() => close());

  const toggleCollectionOpen = (ID: ICollection["ID"]) => {
    setOpenCollections((openCollections) => ({
      ...openCollections,
      [ID]: !openCollections[ID],
    }));
  };

  const handleRemoveCollection = (ID: ICollection["ID"]) => {
    $scope.$root.$apply(() => {
      removeCollection && removeCollection(ID);
    });
  };

  const handleVisibleToggle = (ID: ICollection["ID"]) => {
    $scope.$apply(() => {
      toggleVisible(ID);
    });
  };

  const setTheme = (ID: ICollection["ID"], theme: ITheme) => {
    $scope.$apply(() => {
      setMapTheme(ID, theme);
    });
  };

  const setLabel = (ID: ICollection["ID"], label: string | null) => {
    $scope.$apply(() => {
      setMapLabel(ID, label);
    });
  };

  const addTheme = (ID: ICollection["ID"]) => {
    $scope.$apply(() => {
      ogAddTheme && ogAddTheme(ID);
    });
  };

  const editTheme = (collectionID: ICollection["ID"]) => {
    $scope.$apply(() => {
      ogEditTheme && ogEditTheme(collectionID);
    });
    onClose();
  };

  const cloneTheme = (collectionID: ICollection["ID"], theme: ITheme) => {
    $scope.$apply(() => {
      ogCloneTheme && ogCloneTheme(collectionID, theme);
    });
  };

  const applyFilterChanges = (collectionID: ICollection["ID"], filters: IFilter[]) => {
    $scope.$apply(() => {
      setFilters && setFilters(collectionID, filters);
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!map) return;

    if (active.data.current && active.id !== over?.id) {
      const oldIndex = map.collections.findIndex((c) => c.ID === active.id);
      const newIndex = map.collections.findIndex((c) => c.ID === over?.id);

      if (oldIndex === -1 || oldIndex === undefined || newIndex === -1 || newIndex === undefined) return;

      const newCollections = arrayMove(map.collections ?? [], oldIndex, newIndex);
      $scope.$apply(() => {
        setCollections(newCollections);
      });
    }
  };

  return (
    <div id="side-menu">
      <div className="toggle-triangle open">
        <div className="triangle" onClick={onClose}></div>
      </div>
      <div className="inner">
        <div className="map-tab tab-content" style={selectedCollection ? { width: "0px" } : {}}>
          <div className="input-search centered" style={selectedCollection ? { opacity: 0 } : {}}>
            <input className="search" type="text" placeholder="Search" ng-model="search" />
          </div>
          <div className="collections-wrapper">
            <SortableUl
              className="collections"
              id={`side-collections-sortable`}
              items={map?.collections.map((c) => c.ID) ?? []}
              onDragStart={() => setOpenCollections({})}
              onDragEnd={handleDragEnd}
            >
              {map?.collections.map((collection) => (
                <CollectionLi
                  isOpen={openCollections[collection.ID]}
                  toggleOpen={() => toggleCollectionOpen(collection.ID)}
                  key={`collection-${collection.ID}`}
                  collection={collection}
                  isVisible={map.visible[collection.ID]}
                  label={map.labels[collection.ID]}
                  isPublicPage={isPublicPage}
                  theme={currentThemes[collection.ID]}
                  hasAccess={hasAccess}
                  handleVisibleToggle={handleVisibleToggle}
                  handleRemoveCollection={handleRemoveCollection}
                  editTheme={editTheme}
                  cloneTheme={cloneTheme}
                  setTheme={setTheme}
                  addTheme={addTheme}
                  setLabel={setLabel}
                  onSelect={setSelectedCollection}
                />
              ))}
            </SortableUl>
          </div>
        </div>

        <div className="collections-tab tab-content" style={selectedCollection ? { width: "100%" } : { width: "0px", paddingLeft: "0px" }}>
          {selectedCollection && (
            <>
              <div>
                <button className="btn plain back-button" onClick={() => setSelectedCollection(undefined)} title="Go back to collections list">
                  &lt; Back
                </button>
              </div>
              <TruTerritorySideFilters selectedCollection={selectedCollection} onApplyChanges={applyFilterChanges} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

TruTerritorySide.propTypes = {
  isPublicPage: PropTypes.bool,
  close: PropTypes.func.isRequired,
  removeCollection: PropTypes.func,
  toggleVisible: PropTypes.func.isRequired,
  setMapLabel: PropTypes.func.isRequired,
  setMapTheme: PropTypes.func.isRequired,
  addTheme: PropTypes.func,
  editTheme: PropTypes.func,
  cloneTheme: PropTypes.func,
  setFilters: PropTypes.func,
  setCollections: PropTypes.func.isRequired,
};

export const AngularTruTerritorySide = react2angular(withReduxProvider(TruTerritorySide), Object.keys(TruTerritorySide.propTypes) as (keyof TruTerritorySideProps)[], ["$scope"]);
